<template>
  <div>
    <v-card-subtitle
      class="text-sm pa-0 pt-3 mb-2"
    >
      顧客
    </v-card-subtitle>
    <custom-combobox
      v-model="selectedCustomers"
      :items="customers"
      :filter="filtering"
      :search-input="searchQuery"
      multiple
      chips
      solo
      hide-details
      hide-selected
      dense
      return-object
      :menu-props="{ auto: true, offsetY: true }"
      :prepend-inner-icon="icons.mdiAccountMultiple"
      :loading="isLoading"
      no-data-text="顧客が見つかりません"
      :item-value="getCustomerId"
      :item-text="getCustomerName"
      @change="select"
      @update:search-input="onSearchInputUpdate"
    >
      <template #selection="{ attrs, item }">
        <v-chip
          v-bind="attrs"
          small
          close
          :color="isToDestroy(item.id) ? 'error' : ''"
          :class="isToDestroy(item.id) ? 'v-chip-light-bg error--text' : ''"
          @click:close="remove(item)"
        >
          {{ getCustomerName(item) }}
          <span v-if="item.attributes && item.attributes.memberNumber">({{ item.attributes.memberNumber }})</span>
        </v-chip>
      </template>

      <template #item="{ item }">
        <v-list-item-action>
          <v-checkbox
            hide-details
            disabled
            :ripple="false"
          />
        </v-list-item-action>

        <v-list-item-content>
          <v-list-item-title>
            {{ getCustomerName(item) }}
            <span
              v-if="item.attributes && item.attributes.memberNumber"
              class="text--secondary"
            >(ID:{{ item.attributes.memberNumber }})</span>
          </v-list-item-title>
          <v-list-item-subtitle v-if="item.attributes && item.attributes.memo">
            {{ item.attributes.memo }}
          </v-list-item-subtitle>
        </v-list-item-content>
      </template>
    </custom-combobox>
  </div>
</template>

<script>
import {
  ref, watch, getCurrentInstance,
} from '@vue/composition-api'
import { mdiAccountMultiple } from '@mdi/js'
import {
  filter,
  each,
  findIndex,
  includes,
  map,
  debounce,
} from 'lodash'
import TableApi from '@/api/waiter/Table'
import CustomerApi from '@/api/waiter/Customer'
import CustomCombobox from '@/views/components/custom_vuetify/CustomCombobox'

export default {
  components: {
    CustomCombobox,
  },
  props: {
    tableId: {
      type: [Number, String],
      default: null,
      validator: value => !Number.isNaN(Number(value)),
    },
    tableCustomers: {
      type: Array,
      default: () => [],
    },
    updateEveryTime: {
      type: Boolean,
      default: true,
    },
  },
  setup(props, { emit }) {
    const vm = getCurrentInstance().proxy

    const isLoading = ref(false)
    const selectedCustomers = ref([])
    const customers = ref([])
    const searchQuery = ref('')
    const customerIdsToDestroy = ref([])

    const isToDestroy = customerId => {
      return includes(customerIdsToDestroy.value, Number(customerId))
    }

    const getCustomerId = item => {
      return Number(item.id)
    }

    const getCustomerName = item => {
      return item?.attributes?.name || item?.name || ''
    }

    const fetchCustomers = async (query = '') => {
      isLoading.value = true
      try {
        const res = await CustomerApi.getCustomers({ q: query })
        if (res?.data?.data) {
          customers.value = res.data.data
        }
      } catch (error) {
        console.error('Failed to fetch customers:', error)
      } finally {
        isLoading.value = false
      }
    }

    const debouncedFetchCustomers = debounce(query => {
      fetchCustomers(query)
    }, 300)

    const update = async customerIds => {
      const res = await TableApi.updateTable({
        tableId: props.tableId,
        params: {
          customerIds,
        },
      })

      if (res?.data?.data) return res.data.data

      return res
    }

    const filtering = (item, queryText) => {
      if (!queryText) return true

      const name = item?.attributes?.name?.toString().toLowerCase() || ''
      const memo = item?.attributes?.memo?.toString().toLowerCase() || ''
      const memberNumber = item?.attributes?.memberNumber?.toString() || ''
      const query = queryText.toString().toLowerCase()

      return name.includes(query) || memo.includes(query) || memberNumber.includes(query)
    }

    const select = async items => {
      const validItems = filter(items, item => item && item.id)
      selectedCustomers.value = validItems

      const customerIds = map(validItems, item => Number(item.id))

      if (props.tableId && props.updateEveryTime) {
        const data = await update(customerIds)
        if (data) {
          emit('updated:table', data)
          vm.$toast.success('顧客を更新しました')
        }
      }

      emit('updated', customerIds)
    }

    const remove = async item => {
      const removeTargetIdx = findIndex(
        selectedCustomers.value,
        obj => Number(obj.id) === Number(item.id),
      )

      if (removeTargetIdx === -1) return

      const removeTarget = selectedCustomers.value[removeTargetIdx]

      if (props.updateEveryTime) {
        // Mark for destruction visually
        if (!isToDestroy(removeTarget.id)) {
          customerIdsToDestroy.value.push(Number(removeTarget.id))
        } else {
          // Toggle off destruction
          const idx = customerIdsToDestroy.value.indexOf(Number(removeTarget.id))
          if (idx !== -1) {
            customerIdsToDestroy.value.splice(idx, 1)
          }
        }

        // Actually remove
        selectedCustomers.value.splice(removeTargetIdx, 1)
        const customerIds = map(selectedCustomers.value, c => Number(c.id))

        if (props.tableId) {
          const data = await update(customerIds)
          if (data) {
            emit('updated:table', data)
            vm.$toast.success('顧客を削除しました')
          }
        }

        // Clear destruction marking
        const destroyIdx = customerIdsToDestroy.value.indexOf(Number(removeTarget.id))
        if (destroyIdx !== -1) {
          customerIdsToDestroy.value.splice(destroyIdx, 1)
        }
      } else {
        // Just remove locally
        selectedCustomers.value.splice(removeTargetIdx, 1)
      }

      emit('updated', map(selectedCustomers.value, c => Number(c.id)))
    }

    const onSearchInputUpdate = value => {
      searchQuery.value = value
    }

    const init = tableCustomers => {
      selectedCustomers.value = []
      customerIdsToDestroy.value = []

      if (tableCustomers && tableCustomers.length > 0) {
        each(tableCustomers, customer => {
          // Format: { id, name } from table serializer
          selectedCustomers.value.push({
            id: customer.id,
            attributes: {
              name: customer.name,
              memberNumber: customer.memberNumber,
            },
          })

          // Also add to customers list for display
          if (!customers.value.find(c => Number(c.id) === Number(customer.id))) {
            customers.value.push({
              id: customer.id,
              attributes: {
                name: customer.name,
                memberNumber: customer.memberNumber,
              },
            })
          }
        })
      }
    }

    // Watch search query for API search
    watch(
      () => searchQuery.value,
      newVal => {
        if (newVal && newVal.length >= 1) {
          debouncedFetchCustomers(newVal)
        }
      },
    )

    // Initialize with existing table customers
    init(props.tableCustomers)
    emit('init:init-func', init)

    // Fetch initial customers
    fetchCustomers()

    return {
      // data
      isLoading,
      customers,
      selectedCustomers,
      searchQuery,

      // computed
      customerIdsToDestroy,

      // methods
      getCustomerId,
      getCustomerName,
      select,
      remove,
      isToDestroy,
      filtering,
      onSearchInputUpdate,

      icons: {
        mdiAccountMultiple,
      },
    }
  },
}
</script>
